import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import SEOHead from '@/components/SEOHead';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumb from '@/components/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { comments } from '@/mocks/comments';
import NotFound from '@/pages/NotFound';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);
  const [showToast, setShowToast] = useState(false);

  if (!article) {
    return <NotFound />;
  }

  const category = categories.find((c) => c.id === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);
  const articleUrl = `http://94exue.cn/article/${article.id}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(articleUrl);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  };

  const formatHotValue = (val: number) => {
    if (val >= 10000) return `${(val / 10000).toFixed(1)}万`;
    return val.toLocaleString();
  };

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.summary,
        image: article.image,
        author: {
          '@type': 'Person',
          name: article.author,
        },
        publisher: {
          '@type': 'Organization',
          name: '吃瓜爆料',
          logo: {
            '@type': 'ImageObject',
            url: 'http://94exue.cn/logo.png',
          },
        },
        datePublished: article.publishDate,
        dateModified: article.publishDate,
        articleSection: article.categoryName,
        keywords: article.tags.join(','),
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': articleUrl,
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: 'http://94exue.cn/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: article.categoryName,
            item: `http://94exue.cn/category/${category?.slug || ''}`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: article.title,
            item: articleUrl,
          },
        ],
      },
    ],
  };

  const articleFaqs = [
    {
      question: `这篇关于${article.title.substring(0, 20)}...的爆料可信吗？`,
      answer: '吃瓜爆料平台所有内容均来自公开信息和知情人士爆料，我们致力于提供及时、准确的吃瓜资讯，但请读者保持理性判断。',
    },
    {
      question: '如何在吃瓜爆料投稿爆料？',
      answer: '欢迎通过关于我们页面的联系方式向我们投稿，我们会对爆料内容进行核实后发布。',
    },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: articleFaqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 吃瓜爆料`}
        description={article.summary}
        canonical={articleUrl}
        ogType="article"
        ogImage={article.image}
        articlePublishedTime={article.publishDate}
        articleModifiedTime={article.publishDate}
        articleAuthor={article.author}
        articleSection={article.categoryName}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-50 border-b border-background-200/50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                {
                  label: article.categoryName,
                  href: `/category/${category?.slug || ''}`,
                },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2">
                <span className="inline-flex items-center px-3 py-1.5 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full mb-4">
                  {article.categoryName}
                </span>

                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-snug mb-4">
                  {article.title}
                </h1>

                <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-6">
                  {article.summary}
                </p>

                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-background-200/70">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{article.author}</p>
                    <p className="text-xs text-foreground-400">
                      {article.publishDate} · {article.readTime}阅读
                    </p>
                  </div>
                  <div className="ml-auto flex items-center gap-3">
                    <button
                      onClick={handleCopyLink}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors"
                      aria-label="复制链接"
                    >
                      <i className="ri-share-line" />
                    </button>
                    <span className="flex items-center gap-1 text-xs text-foreground-400">
                      <i className="ri-fire-line text-primary-500" />
                      {formatHotValue(article.hotValue)}
                    </span>
                  </div>
                </div>

                <div className="relative rounded-xl overflow-hidden mb-8">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full aspect-[16/9] object-cover object-top"
                    title={`${article.title} - 吃瓜爆料`}
                  />
                </div>

                <div
                  className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200/70">
                  {article.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/category/${category?.slug || ''}`}
                      className="px-3 py-1.5 bg-background-100 text-foreground-600 text-xs rounded-full hover:bg-primary-100 hover:text-primary-600 transition-colors"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>

                {/* FAQ Section */}
                <section className="mt-10 pt-8 border-t border-background-200/70">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                    常见问题
                  </h2>
                  <div className="space-y-4">
                    {articleFaqs.map((faq, index) => (
                      <div key={index} className="p-4 md:p-5 rounded-xl bg-background-100 border border-background-200/50">
                        <h3 className="text-sm font-semibold text-foreground-800 mb-2">
                          {faq.question}
                        </h3>
                        <p className="text-xs text-foreground-500 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>
              </div>

              {/* Sidebar */}
              <aside className="space-y-6">
                {/* Author Card */}
                <div className="p-5 md:p-6 rounded-xl bg-foreground-800 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs text-white/50">编辑精选</span>
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-white/30" />
                      <span className="w-1.5 h-1.5 rounded-full bg-white/30" />
                      <span className="w-1.5 h-1.5 rounded-full bg-white/30" />
                    </div>
                  </div>
                  <p className="text-base md:text-lg font-medium leading-relaxed mb-6 text-white/90">
                    {article.summary}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <img
                        src={article.authorAvatar}
                        alt={article.author}
                        className="w-9 h-9 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-xs font-medium">{article.author}</p>
                        <p className="text-xs text-white/50">资深爆料编辑</p>
                      </div>
                    </div>
                    <Link
                      to="/about"
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/15 text-white hover:bg-white/25 transition-colors"
                    >
                      <i className="ri-arrow-right-line text-sm" />
                    </Link>
                  </div>
                </div>

                {/* Related Articles */}
                <div className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-4">
                    相关推荐
                  </h3>
                  <div className="space-y-4">
                    {relatedArticles.map((related) => (
                      <Link
                        key={related.id}
                        to={`/article/${related.id}`}
                        className="flex gap-3 group"
                      >
                        <div className="w-20 h-14 flex-shrink-0 rounded-lg overflow-hidden">
                          <img
                            src={related.image}
                            alt={related.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="min-w-0">
                          <h4 className="text-xs font-medium text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                            {related.title}
                          </h4>
                          <p className="text-xs text-foreground-400 mt-1">
                            {related.publishDate}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Hot Tags */}
                <div className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-4">
                    热门标签
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {['顶流男星', '网红翻车', '出轨实锤', '选秀黑幕', '高校丑闻', '带货造假', '隐婚生子', '时间管理'].map((tag) => (
                      <Link
                        key={tag}
                        to="/category/celebrity"
                        className="px-2.5 py-1 bg-secondary-50 text-secondary-700 text-xs rounded-full hover:bg-secondary-100 transition-colors"
                      >
                        {tag}
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </article>

        {/* Comments Section */}
        <section className="bg-secondary-50 py-12 md:py-16 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-3">
                <i className="ri-chat-3-line" />
                瓜友热议
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                大家都在说什么
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {comments.slice(0, 6).map((comment, index) => (
                <div
                  key={comment.id}
                  className={`p-5 md:p-6 rounded-xl ${
                    index % 3 === 0
                      ? 'bg-background-50 border border-background-200/70'
                      : index % 3 === 1
                        ? 'bg-accent-500 text-white'
                        : 'bg-primary-500 text-white'
                  }`}
                >
                  <div className="mb-4">
                    <i
                      className={`ri-double-quotes-l text-2xl ${
                        index % 3 === 0 ? 'text-primary-300' : 'text-white/40'
                      }`}
                    />
                  </div>
                  <p
                    className={`text-sm leading-relaxed mb-5 ${
                      index % 3 === 0 ? 'text-foreground-600' : 'text-white/90'
                    }`}
                  >
                    {comment.content}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <img
                        src={comment.avatar}
                        alt={comment.author}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                      <div>
                        <p
                          className={`text-xs font-medium ${
                            index % 3 === 0 ? 'text-foreground-700' : 'text-white'
                          }`}
                        >
                          {comment.author}
                        </p>
                        <p
                          className={`text-xs ${
                            index % 3 === 0 ? 'text-foreground-400' : 'text-white/60'
                          }`}
                        >
                          {comment.date}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <i
                        className={`ri-heart-fill text-xs ${
                          index % 3 === 0 ? 'text-primary-500' : 'text-white/60'
                        }`}
                      />
                      <span
                        className={`text-xs ${
                          index % 3 === 0 ? 'text-foreground-500' : 'text-white/60'
                        }`}
                      >
                        {comment.likes}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Toast */}
      {showToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 bg-foreground-800 text-white text-sm rounded-full shadow-lg">
          链接已复制到剪贴板
        </div>
      )}
    </>
  );
}
